'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocationOrdersFilters } from '../../hooks/useLocationOrdersFilters';

interface LocationOrdersFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

/** Campo de filtro con label */
const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Órdenes de ubicación.
 * Estado (done, pending, in_progress, cancelled) y rango de fechas.
 */
export const LocationOrdersFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: LocationOrdersFiltersPanelProps) => {
  const { t } = useTranslation('locationordersModule');

  const { filters, handleStatusChange, handleDateRangeChange, clearAllFilters } = useLocationOrdersFilters({
    initialConditions,
    onChange,
  });

  const statusOptions = useMemo(
    () => [
      { label: t('filters.options.status.done'), value: 'done' },
      { label: t('filters.options.status.pending'), value: 'pending' },
      { label: t('filters.options.status.in_progress'), value: 'in_progress' },
      { label: t('filters.options.status.cancelled'), value: 'cancelled' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.dateRange?.from && filters.dateRange?.to) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filters.status', 'Estado')}>
          <Combobox
            options={statusOptions}
            value={filters.status}
            setValue={handleStatusChange}
            placeholder={t('filters.select_placeholder', 'Seleccionar...')}
            dataTestId="filter-status"
          />
        </FilterField>

        <FilterField label={t('filters.date', 'Fecha')}>
          <DateRangePicker
            initialDateFrom={filters.dateRange?.from}
            initialDateTo={filters.dateRange?.to}
            onUpdate={({ range }) => handleDateRangeChange(range)}
            placeholder={t('filters.dateRangePlaceholder', 'Seleccionar rango de fechas')}
            dataTestId="filter-created-at"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="location-orders-clear-filters"
          >
            {t('filters.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
